import React from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import { flow, get } from 'lodash-es';
import { connect } from 'react-redux';

// Models
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { CmsEntities, bindToContent } from 'client/data/models/cms';
import { getBestRankings, SEOModel } from 'client/data/models/seo';
import { DEFAULT_CONTENT } from 'client/data/cms/content';

// Utils
import { getBreadcrumbName, getUrlSlugByName } from 'site-modules/shared/utils/upper-funnel/type-mapping';
import { getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getCustomSubtypeTitle } from 'site-modules/upper-funnel/utils/seo-content';

import { TopRatedPromoUnit } from 'site-modules/shared/components/top-rated-promo-unit/top-rated-promo-unit';
import { HeroContent } from 'site-modules/upper-funnel/components/type/hero-content/hero-content';
import { withCompareVehicleTray } from 'site-modules/shared/components/compare-vehicle-tray-wrapper/compare-vehicle-tray-wrapper';
import { VehicleTypeRankingsList } from 'site-modules/upper-funnel/components/type/vehicle-type-rankings-list/vehicle-type-rankings-list';
import { getSubtypeDisplayTitle } from 'site-modules/upper-funnel/utils/subtype-display';
import {
  getIndicesLength,
  getSubtypeOverflowAdPos,
} from 'site-modules/upper-funnel/pages/subtype/get-subtype-overflow-ad-pos';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { MostSoldTypeInfographic } from 'site-modules/shared/components/upper-funnel/most-sold-type-infographic/most-sold-type-infographic';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import {
  hasMostSoldTypeContent,
  mappedIndicesForSubtypeAdPlacement,
} from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { getSubtypeContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';
import { ExcoVideoViewer } from 'site-modules/shared/components/exco-video/exco-video-viewer';

import './best-rankings.scss';

export const VehicleTypeRankingsListWithCompareTray = withCompareVehicleTray(VehicleTypeRankingsList, {
  backFocusLink: {
    label: 'Skip to vehicle rankings',
    selector: '.vehicle-card .compare-checkbox input',
  },
});

const getRefererPathName = referer => (referer ? get(referer.match(/edmunds.com(.*)/), '[1]', '') : '');

function BestRankingsPageUI({ isMobile = false, referer = '', bestRankings = [], content = DEFAULT_CONTENT }) {
  const rankings = bestRankings[0];
  const subtypeAdPlacementIndices = mappedIndicesForSubtypeAdPlacement(bestRankings);
  const type = get(rankings, 'edmundsTypeCategory', '').toLowerCase();
  const subtype = get(rankings, 'subtype');
  const subtypeExpanisonName = get(rankings, 'displayName');
  const title = getCustomSubtypeTitle(type, subtype, subtypeExpanisonName);
  const mostSoldTypeUniqueName = `${subtype}-${type}s`.toLowerCase();
  const mostSoldTypeGraphTitle = hasMostSoldTypeContent(type, subtype);
  const subheadTitle = getSubtypeDisplayTitle(type, subtype, true);
  const breadcrumbsData =
    type && subtype
      ? [
          getHomeBreadcrumb(),
          {
            title: getBreadcrumbName(type),
            href: `/${getUrlSlugByName(type)}`,
          },
          {
            title: subtype.replace(/suvs/g, 'SUVs'),
          },
        ]
      : [];
  const trkParam = getRefererPathName(referer) === '/' ? { trk: type } : {};

  const description = content.metadata('description').value();

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="type-page-v2 subtype-page-v2"
      role="main"
      aria-labelledby="type-page-heading"
    >
      <HeroContent
        breadcrumbsData={breadcrumbsData}
        isMobile={isMobile}
        type={type}
        title={title}
        isChildPage
        subtypeTitle={subheadTitle}
      />
      <Container className="mt-2 mb-2">
        <TopRatedPromoUnit className="mt-2 mb-2" isMobile={isMobile} />
        <ExcoVideoViewer />
        <VehicleTypeRankingsListWithCompareTray
          type={type}
          trkParam={trkParam}
          vehicleEditorialRankings={bestRankings}
          isSubtypePage
          isSubtypeExpansionPage
          subtypeAdPlacementIndices={subtypeAdPlacementIndices}
        />
        <div className="d-flex flex-column">
          {getSubtypeOverflowAdPos(getIndicesLength(subtypeAdPlacementIndices), trkParam, isMobile, type)}
        </div>
        {mostSoldTypeGraphTitle && (
          <ScrollElement id={`best-selling-${mostSoldTypeUniqueName}`}>
            <RenderWhenViewable
              verticalOffset="100%"
              placeholder={
                <MostSoldTypeInfographic type={mostSoldTypeUniqueName} isMobile={isMobile} showMap={false} />
              }
            >
              <MostSoldTypeInfographic type={mostSoldTypeUniqueName} isMobile={isMobile} />
            </RenderWhenViewable>
          </ScrollElement>
        )}
        {description && <ContentFragment classes="text-gray-darker pb-3 description">{description}</ContentFragment>}
      </Container>
    </main>
  );
}

BestRankingsPageUI.propTypes = {
  isMobile: PropTypes.bool,
  referer: PropTypes.string,
  bestRankings: PropTypes.arrayOf(TypeRankingsEntities.VehicleRanking),
  content: CmsEntities.Content,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  referer: get(state.pageContext.page, 'referer'),
});

export const BestRankingsPage = flow(
  component =>
    connectToModel(component, {
      content: bindToContent(props => {
        const rankings = get(props, 'bestRankings.[0]');
        const type = get(rankings, 'edmundsTypeCategory', '').toLowerCase();
        const subtype = get(rankings, 'subtype');
        return getSubtypeContentPath(type, subtype);
      }),
    }),
  component =>
    connectToModel(component, {
      bestRankings: bindToPath(getBestRankings(), SEOModel),
    })
)(connect(mapStateToProps)(BestRankingsPageUI));
